import { useAtom } from 'jotai';
import { atom } from 'jotai';
import { useCallback } from 'react';
import { ExternalLink, ExpandIcon } from 'lucide-react';

import { Drawer, DrawerContent } from '@rocco/components/drawer';
import {
    PhotoDocumentViewerModal,
    type ViewerItem,
} from '@rocco/components/photo-document-viewer';
import { usePhotoDocumentViewer } from '@rocco/hooks/use-photo-document-viewer';
import { Comments } from '../../../../build-hub-comment/components/Comments';
import type { DetailedPinWithChecked } from '~/modules/design-board/component/all-pins/AllPins';
import { Tile } from '@rocco/ui/tile-shared/components/Tile';
import { isArticleTileViewModel } from '@rocco/ui/tile-article/view-models/article';
import { VotingControl } from './voting-control';
import { useBuildHubBoardItemVoting } from '../../../hooks/use-build-hub-board-item-voting';
import { ReactionTypeEnum } from '../../../../../../generated/graphql';
import { Button } from '@rocco/components/button';
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from '@rocco/components/tooltip';
import { cn } from '@rocco/utils/cn';

interface ItemDetailDrawerAtomType {
    pin: DetailedPinWithChecked;
    scrollToCommentId?: number;
}

/**
 * Atom to store the currently selected item ID for the detail drawer
 * When null, the drawer is closed. When a number, the drawer is open for that item.
 * Optional scrollToCommentId for deep linking to specific comments.
 */
const moodBoardItemDetailDrawerAtom = atom<ItemDetailDrawerAtomType | null>(
    null
);

/**
 * UI component for the item detail drawer
 * Renders the drawer with comments when an item is selected
 */
export const MoodboardItemDetailDrawer = () => {
    const [item, setItem] = useAtom(moodBoardItemDetailDrawerAtom);

    const handleOpenChange = (open: boolean) => {
        if (!open) {
            setItem(null);
        }
    };

    if (!item) return null;

    const pin = item.pin;
    const itemId = pin?.ID;
    const scrollToCommentId = item.scrollToCommentId;

    return (
        <Drawer
            open={!!itemId}
            onOpenChange={handleOpenChange}
            direction="right"
        >
            <DrawerContent
                className="data-[vaul-drawer-direction=right]:sm:max-w-[33.25rem]"
                variant="default"
                overlayVariant="dark"
                noHandle={true}
                hideDefaultCloseButton={true}
                scrollable={true}
                //closeButtonClassName="md:top-4 md:right-4"
            >
                {itemId && (
                    <ItemDetailDrawerContent
                        pin={pin}
                        scrollToCommentId={scrollToCommentId}
                    />
                )}
            </DrawerContent>
        </Drawer>
    );
};

const ItemDetailDrawerContent = ({
    pin,
    scrollToCommentId,
}: {
    pin: DetailedPinWithChecked;
    scrollToCommentId?: number;
}) => {
    const isArticleTile = isArticleTileViewModel(pin.TileData);
    const { isOpen, items, openViewer, closeViewer } = usePhotoDocumentViewer();

    // Type assertion to include ExternalUrlContent which is BuildHub-specific
    const Target = pin.Target as
        | DetailedPinWithChecked['Target']
        | {
              __typename: 'ExternalUrlContent';
              SourceUrl?: string | null;
              Title?: string | null;
          };
    const isExternalUrl = Target.__typename === 'ExternalUrlContent';
    const isLibraryImage = Target.__typename === 'LibraryImage';
    const libraryImageProjectLink =
        isLibraryImage &&
        Target.__typename === 'LibraryImage' &&
        Target.LibraryItem?.Link
            ? Target.LibraryItem.Link
            : null;

    const {
        handleUpVote,
        handleDownVote,
        handleRevokeVote,
        upVoteCollaborators,
        downVoteCollaborators,
        isVotedUp,
        isVotedDown,
    } = useBuildHubBoardItemVoting({
        targetId: pin.ID,
    });

    const handleVoteUp = useCallback(() => {
        if (isVotedUp) {
            handleRevokeVote(ReactionTypeEnum.UpVote);
        } else {
            handleUpVote();
        }
    }, [isVotedUp, handleRevokeVote, handleUpVote]);

    const handleVoteDown = useCallback(() => {
        if (isVotedDown) {
            handleRevokeVote(ReactionTypeEnum.DownVote);
        } else {
            handleDownVote();
        }
    }, [isVotedDown, handleRevokeVote, handleDownVote]);

    /**
     * Opens the external URL in a new tab
     */
    const handleExternalLinkClick = useCallback(
        (e?: React.MouseEvent) => {
            if (isExternalUrl && Target.__typename === 'ExternalUrlContent') {
                const url = Target.SourceUrl;
                if (url) {
                    e?.preventDefault();
                    e?.stopPropagation();
                    window.open(url, '_blank', 'noopener,noreferrer');
                }
            }
        },
        [isExternalUrl, Target]
    );

    /**
     * Opens the LibraryImage project link in a new tab
     */
    const handleLibraryImageProjectLinkClick = useCallback(
        (e?: React.MouseEvent) => {
            if (libraryImageProjectLink) {
                e?.preventDefault();
                e?.stopPropagation();
                window.open(
                    libraryImageProjectLink,
                    '_blank',
                    'noopener,noreferrer'
                );
            }
        },
        [libraryImageProjectLink]
    );

    const handleExpandButtonClick = useCallback(() => {
        // Convert pin images to viewer items based on tile type
        const viewerItems: ViewerItem[] = [];

        if (Target.__typename === 'MemberUpload') {
            const filename = Target.FileName;
            const isImage =
                filename &&
                (filename.endsWith('.jpg') ||
                    filename.endsWith('.jpeg') ||
                    filename.endsWith('.png') ||
                    filename.endsWith('.gif') ||
                    filename.endsWith('.bmp') ||
                    filename.endsWith('.tiff') ||
                    filename.endsWith('.ico') ||
                    filename.endsWith('.webp'));
            const isPDF = filename && filename.endsWith('.pdf');
            const isVideo =
                filename &&
                (filename.endsWith('.mp4') ||
                    filename.endsWith('.mov') ||
                    filename.endsWith('.avi') ||
                    filename.endsWith('.mkv'));
            const isUnsupportedFile = !isImage && !isPDF && !isVideo;

            if (isImage) {
                viewerItems.push({
                    id: `contentViewer_${pin.ID}`,
                    url: Target.Link,
                    title: Target.Title,
                    type: 'image',
                });
            } else if (isVideo) {
                viewerItems.push({
                    id: `contentViewer_${pin.ID}`,
                    url: Target.Link,
                    title: Target.Title,
                    type: 'video',
                });
            } else if (isPDF) {
                viewerItems.push({
                    id: `contentViewer_${pin.ID}`,
                    url: Target.Link,
                    title: Target.Title,
                    type: 'document',
                });
            } else if (isUnsupportedFile) {
                viewerItems.push({
                    id: `contentViewer_${pin.ID}`,
                    url: Target.Link,
                    title: Target.Title,
                    type: 'file',
                });
            }
        }

        if (Target.__typename === 'LibraryImage') {
            // Use the same HD image fields as GalleryModule for consistency
            // Match the exact logic from getImageUrlForColumn in map-module-data-columns.server.ts
            const imageFile = Target.LibraryImageFile;
            const isProjectContext = !!Target.LibraryItem;

            let hdImageUrl: string | undefined;
            if (isProjectContext) {
                // For project contexts, use FrontendDetailPage (no watermarks) - same as GalleryModule
                hdImageUrl = imageFile?.FrontendDetailPage?.Desktop
                    ? String(imageFile.FrontendDetailPage.Desktop)
                    : undefined;
            } else {
                // For other contexts, use DetailPageStandard (with watermarks)
                hdImageUrl = imageFile?.DetailPageStandard?.Desktop
                    ? String(imageFile.DetailPageStandard.Desktop)
                    : undefined;
            }

            // Only add to viewer if HD image is available
            if (hdImageUrl) {
                viewerItems.push({
                    id: `libraryImageViewer_${pin.ID}`,
                    url: hdImageUrl,
                    title: Target.LibraryItem?.Title || pin.Title,
                    type: 'image',
                });
            }
        }

        if (viewerItems.length > 0) {
            openViewer(viewerItems);
        }
    }, [Target, pin.ID, pin.Title, openViewer]);

    /**
     * Custom action buttons overlay
     * - For external URLs: shows external link icon instead of expand icon
     * - For LibraryImage with project link: shows both expand button and external link button side by side
     */
    const customActionButtonsOverlay = isExternalUrl ? (
        <div className="absolute right-4 top-3.5 z-10 flex gap-1 transition-opacity duration-300">
            <Tooltip>
                <TooltipTrigger asChild>
                    <Button
                        type="button"
                        size="square42"
                        color="secondary70"
                        onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            handleExternalLinkClick();
                        }}
                        aria-label="Open external link in new tab"
                    >
                        <ExternalLink className={cn('size-4')} />
                    </Button>
                </TooltipTrigger>
                <TooltipContent>
                    <p>Open external link</p>
                </TooltipContent>
            </Tooltip>
        </div>
    ) : libraryImageProjectLink ? (
        <div className="absolute right-4 top-3.5 z-10 flex gap-2 transition-opacity duration-300">
            <Tooltip>
                <TooltipTrigger asChild>
                    <Button
                        type="button"
                        size="square42"
                        color="secondary70"
                        onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            handleExpandButtonClick();
                        }}
                        aria-label="Expand photo"
                    >
                        <ExpandIcon className={cn('size-4')} />
                    </Button>
                </TooltipTrigger>
                <TooltipContent>
                    <p>Expand photo</p>
                </TooltipContent>
            </Tooltip>
            <Tooltip>
                <TooltipTrigger asChild>
                    <Button
                        type="button"
                        size="square42"
                        color="secondary70"
                        onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            handleLibraryImageProjectLinkClick();
                        }}
                        aria-label="Open project page in new tab"
                    >
                        <ExternalLink className={cn('size-4')} />
                    </Button>
                </TooltipTrigger>
                <TooltipContent>
                    <p>Open project page</p>
                </TooltipContent>
            </Tooltip>
        </div>
    ) : undefined;

    // For BuildHub pins, use the custom title if available (overrides server-generated TileData.title)
    const tileModel = pin.Title
        ? { ...pin.TileData, title: pin.Title }
        : pin.TileData;

    return (
        <TooltipProvider>
            <div className="flex h-full flex-col pt-4">
                {/* Pin details */}
                <div className="relative flex-shrink-0 px-4 pb-4">
                    <div className="relative">
                        <Tile
                            tile={{
                                aspectRatio: 'default',
                                model: {
                                    ...tileModel,
                                    badge: null,
                                    ...(isArticleTile && {
                                        variant: 'default',
                                    }),
                                },
                                hideFollowButton: true,
                                hideServiceTags: true,
                                // For external URLs: make tile clickable to open link
                                // For LibraryImage with project link: custom overlay handles both buttons
                                // For other types: show expand button for viewer
                                disableLink: !!(
                                    isExternalUrl || libraryImageProjectLink
                                ),
                                onClick: isExternalUrl
                                    ? handleExternalLinkClick
                                    : undefined,
                                onExpandButtonClick:
                                    isExternalUrl || libraryImageProjectLink
                                        ? undefined
                                        : handleExpandButtonClick,
                                className: cn(
                                    '[&_.rocco-tile-description-block]:max-w-[75%]',
                                    isExternalUrl && 'cursor-pointer'
                                ),
                            }}
                        />
                        {customActionButtonsOverlay}
                    </div>
                    <div className="absolute bottom-4 right-0 px-4">
                        <VotingControl
                            hasVotedUp={isVotedUp}
                            hasVotedDown={isVotedDown}
                            upVoteCollaborators={upVoteCollaborators}
                            downVoteCollaborators={downVoteCollaborators}
                            onVoteUp={handleVoteUp}
                            onVoteDown={handleVoteDown}
                        />
                    </div>
                </div>
                <div className="mx-4 mb-4 h-px bg-[#E4E5E4]" />
                {/* Comment section */}
                <div className="mx-4 min-h-0 flex-1">
                    <Comments
                        itemId={pin.ID}
                        unreadCount={pin.unreadCount ?? 0}
                        scrollToCommentId={scrollToCommentId}
                    />
                </div>

                {/* Photo/Document Viewer Modal */}
                <PhotoDocumentViewerModal
                    open={isOpen}
                    onOpenChange={closeViewer}
                    items={items}
                    title="Pin Gallery"
                />
            </div>
        </TooltipProvider>
    );
};

/**
 * Custom hook to manage the item detail drawer state
 * Provides functions to open and close the drawer, and access to the current item ID
 */
export const useMoodboardItemDetailDrawer = () => {
    const [item, setItem] = useAtom(moodBoardItemDetailDrawerAtom);

    const openDialog = (
        pin: DetailedPinWithChecked,
        scrollToCommentId?: number
    ) => {
        setItem({ pin, scrollToCommentId });
    };

    const closeDialog = () => {
        setItem(null);
    };

    return {
        item: item?.pin,
        openDialog,
        closeDialog,
        isOpen: !!item?.pin,
    };
};
